import React, { useState, useCallback, useEffect } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseForm } from "@app/components/common/forms/BaseForm/BaseForm";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { BasePopconfirm } from "@app/components/common/BasePopconfirm/BasePopconfirm";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import {
  message,
  Modal,
  Input,
  DatePicker,
  Select,
  Switch,
  Form,
  Row,
  Col,
  Alert,
  Spin,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { adminApi, Company, CompanyPageParams } from "@app/api/admin";
import dayjs from "dayjs";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import { QRCodeSVG } from "qrcode.react";
import { BaseDatePicker } from "@app/components/common/pickers/BaseDatePicker";

export interface initialPaginationProps {
  page: number;
  page_size: number;
  total?: number;
}

export const initialPagination: initialPaginationProps = {
  page: 1,
  page_size: 10,
  total: 0,
};

const CompanyPage: React.FC = () => {
  const [createForm] = BaseForm.useForm();
  const [searchForm] = Form.useForm();
  const [tableData, setTableData] = useState<{
    data: Company[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [loading, setLoading] = useState(false);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  // 使用 searchParams 替代单独的状态
  const [searchParams, setSearchParams] = useState<{
    keyword?: string;
    status?: number;
  }>({});
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);

  const [isEdit, setIsEdit] = useState(false);

  // 添加分页状态
  const [pagination, setPagination] = useState(initialPagination);

  // 获取公司列表数据
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const { total, ...rest } = pagination;
      const { data } = await adminApi.companies.list({
        ...rest,
        ...searchParams,
      });

      // 直接使用接口返回的数据
      setTableData({
        data: data.companies || [],
        pagination: data.pagination,
      });
    } catch (error: any) {
      console.error(error.message || "获取公司列表失败");
    } finally {
      setLoading(false);
    }
  };

  useUpdateEffect(() => {
    fetchCompanies();
  }, [searchParams, pagination]);

  // 搜索处理 - 更新为使用表单
  const handleSearch = (values: any) => {
    setSearchParams({
      keyword: values.keyword?.trim(),
      status: values.status,
    });
    setPagination({ ...initialPagination, page: 1 });
  };

  // 重置搜索 - 更新为使用表单
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPagination({ ...initialPagination, page: 1 });
  };

  // 状态切换
  const handleStatusToggle = async (record: Company, checked: boolean) => {
    try {
      const newStatus = checked ? 1 : 0;
      await adminApi.companies.updateStatus(record.id, newStatus);

      fetchCompanies();

      message.success(`公司状态已${checked ? "启用" : "禁用"}`);
    } catch (error: any) {
      message.error(error.message || "状态更新失败");
    }
  };

  // 编辑功能
  const handleEdit = async (record: Company) => {
    const { data } = await adminApi.companies.detail(record.id);

    console.log(data);

    createForm.setFieldsValue({
      ...data,
      registration_date: record.registration_date
        ? dayjs(record.registration_date)
        : null,
    });
    setIsEdit(true);
    setIsCreateModalVisible(true);
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      page_size: pageSize,
    });
  };

  // 删除
  const handleDeleteRow = async (id: number) => {
    try {
      await adminApi.companies.delete(id);
      fetchCompanies();

      message.success("删除成功");
    } catch (error: any) {
      message.error(error.message || "删除失败");
    }
  };

  // 生成加入二维码（改为使用静态链接，不再调用接口）
  const handleGenerateQR = async (record: Company) => {
    const url = `https://wechat.yuanfengai.com?id=${record.id}`;
    setCurrentCompany(record || null);
    setQrCode(url);
    setQrModalVisible(true);
  };

  // 下载二维码
  const handleDownloadQR = () => {
    console.log("first", currentCompany);
    // 获取 SVG 元素
    const svg = document.querySelector(".qr-code-svg") as SVGElement;
    if (!svg) {
      message.error("二维码未找到");
      return;
    }

    // 创建 canvas 元素
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      message.error("浏览器不支持 Canvas");
      return;
    }

    // 设置 canvas 尺寸
    canvas.width = 256;
    canvas.height = 256;

    // 将 SVG 转换为图片
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // 绘制到 canvas
      ctx.drawImage(img, 0, 0, 256, 256);

      // 转换为 PNG 并下载
      canvas.toBlob((blob) => {
        if (blob) {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `${currentCompany?.name || "公司"}加入二维码.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }
      }, "image/png");

      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      message.error("二维码转换失败");
      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  const [columns] = useState([
    {
      title: "公司名称",
      dataIndex: "name",
      width: 200,
    },
    {
      title: "法定代表人",
      dataIndex: "legal_representative",
      width: 120,
    },

    {
      title: "联系电话",
      dataIndex: "contact_phone",
      width: 180,
    },
    {
      title: "联系邮箱",
      dataIndex: "contact_email",
      width: 120,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 80,
      render: (status: number, record: Company) => (
        <Switch
          checked={status === 1}
          onChange={(checked) => handleStatusToggle(record, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: "操作",
      dataIndex: "actions",
      width: 180,
      fixed: "right" as const,
      render: (_: any, record: Company) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              size="small"
              onClick={() => handleEdit(record)}
            >
              编辑
            </BaseButton>
            <BaseButton
              type="primary"
              size="small"
              onClick={() => handleGenerateQR(record)}
            >
              生成二维码
            </BaseButton>
            <BasePopconfirm
              title="确定要删除这个公司吗？"
              // description="删除后将无法恢复，请谨慎操作。"
              onConfirm={() => handleDeleteRow(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <BaseButton type="default" size="small" danger>
                删除
              </BaseButton>
            </BasePopconfirm>
          </BaseSpace>
        );
      },
    },
  ]);

  // 新增公司
  const handleAddCompany = () => {
    setIsEdit(false);
    createForm.resetFields();
    setIsCreateModalVisible(true);
  };

  // 提交表单
  const handleCreateSubmit = async () => {
    try {
      setCreateLoading(true);
      const values = await createForm.validateFields();
      console.log("values", values);
      // 处理日期格式
      const submitData = {
        ...values,
        registration_date: values.registration_date
          ? dayjs(values.registration_date).format("YYYY-MM-DD")
          : undefined,
      };
      isEdit
        ? await adminApi.companies.update(values.id, submitData)
        : await adminApi.companies.create(submitData);
      fetchCompanies();
      message.success(isEdit ? "编辑成功" : "创建成功");
      setIsCreateModalVisible(false);
      createForm.resetFields();
    } catch (error: any) {
      console.log(error);
      if (error.errorFields?.length) {
        message.error(error.errorFields?.[0]?.errors?.[0]);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // 关闭弹窗
  const handleCreateCancel = () => {
    setIsCreateModalVisible(false);
    createForm.resetFields();
  };

  return (
    <>
      <PageTitle>公司管理</PageTitle>

      {/* 搜索和过滤区域 - 重构为使用表单 */}
      <Form
        form={searchForm}
        onFinish={handleSearch}
        layout="inline"
        style={{
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]} style={{ width: "100%" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="keyword" style={{ marginBottom: 0 }}>
              <Input placeholder="搜索公司名称、法定代表人" allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="status" style={{ marginBottom: 0 }}>
              <Select
                placeholder="状态筛选"
                allowClear
                style={{ width: "100%" }}
              >
                <Select.Option value={1}>启用</Select.Option>
                <Select.Option value={0}>禁用</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={8} lg={12}>
            <Form.Item style={{ marginBottom: 0 }}>
              <BaseSpace>
                <BaseButton
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </BaseButton>
                <BaseButton onClick={handleReset}>重置</BaseButton>
                <BaseButton type="primary" onClick={handleAddCompany}>
                  新增公司
                </BaseButton>
              </BaseSpace>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        dataSource={tableData.data}
        columns={columns}
        pagination={{
          ...tableData.pagination,
          onChange: handleTableChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1200 }}
      />

      {/* 创建/编辑模态框 - 保持不变 */}
      <Modal
        title={isEdit ? "编辑公司" : "创建公司"}
        visible={isCreateModalVisible}
        onOk={handleCreateSubmit}
        onCancel={handleCreateCancel}
        confirmLoading={createLoading}
        width={800}
        destroyOnClose
      >
        <BaseForm
          form={createForm}
          layout="vertical"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <BaseForm.Item label="公司名称" name="id" hidden>
            <Input />
          </BaseForm.Item>
          <BaseForm.Item
            label="公司名称"
            name="name"
            rules={[{ required: true, message: "请输入公司名称" }]}
          >
            <Input placeholder="请输入公司名称" maxLength={50} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="法定代表人"
            name="legal_representative"
            rules={[{ required: true, message: "请输入法定代表人" }]}
          >
            <Input placeholder="请输入法定代表人" maxLength={50} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="注册号"
            name="registration_number"
            rules={[{ required: true, message: "请输入注册号" }]}
          >
            <Input placeholder="请输入注册号" maxLength={100} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="统一社会信用代码"
            name="unified_social_code"
            rules={[{ required: true, message: "请输入统一社会信用代码" }]}
          >
            <Input
              placeholder="请输入统一社会信用代码"
              maxLength={100}
              showCount
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="公司类型"
            name="company_type"
            rules={[{ required: false, message: "请选择公司类型" }]}
          >
            <Select placeholder="请选择公司类型">
              <Select.Option value="有限责任公司">有限责任公司</Select.Option>
              <Select.Option value="股份有限公司">股份有限公司</Select.Option>
              <Select.Option value="个人独资企业">个人独资企业</Select.Option>
              <Select.Option value="合伙企业">合伙企业</Select.Option>
              <Select.Option value="其他">其他</Select.Option>
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="注册资本"
            name="registered_capital"
            rules={[{ required: false, message: "请输入注册资本" }]}
          >
            <Input
              style={{ width: "100%" }}
              placeholder="请输入注册资本"
              maxLength={100}
              showCount
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="注册日期"
            name="registration_date"
            rules={[{ required: false, message: "请选择注册日期" }]}
          >
            <BaseDatePicker
              style={{ width: "100%" }}
              placeholder="请选择注册日期"
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="经营范围"
            name="business_scope"
            rules={[{ required: false, message: "请输入经营范围" }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入经营范围"
              maxLength={100}
              showCount
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="注册地址"
            name="registered_address"
            rules={[{ required: false, message: "请输入注册地址" }]}
          >
            <Input placeholder="请输入注册地址" maxLength={500} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="经营地址"
            name="operating_address"
            rules={[{ required: false, message: "请输入经营地址" }]}
          >
            <Input placeholder="请输入经营地址" maxLength={500} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="联系电话"
            name="contact_phone"
            rules={[{ required: true, message: "请输入联系电话" }]}
          >
            <Input placeholder="请输入联系电话" maxLength={50} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="联系邮箱"
            name="contact_email"
            rules={[
              { required: false, message: "请输入联系邮箱" },

              { type: "email", message: "请输入有效的邮箱地址" },
            ]}
          >
            <Input placeholder="请输入联系邮箱" maxLength={100} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="官网"
            name="website"
            rules={[
              { required: false, message: "请输入官网" },

              { type: "url", message: "请输入有效的网址" },
            ]}
          >
            <Input placeholder="请输入官网地址" maxLength={200} showCount />
          </BaseForm.Item>

          <BaseForm.Item label="备注" name="remark">
            <Input.TextArea
              rows={3}
              placeholder="请输入备注信息"
              maxLength={200}
              showCount
            />
          </BaseForm.Item>
        </BaseForm>
      </Modal>

      <Modal
        title="公司加入二维码"
        visible={qrModalVisible}
        onCancel={() => {
          setQrModalVisible(false);
          setCurrentCompany(null);
        }}
        footer={
          <BaseSpace>
            <BaseButton
              key="download"
              type="primary"
              onClick={() => handleDownloadQR()}
            >
              下载二维码
            </BaseButton>

            <BaseButton key="close" onClick={() => setQrModalVisible(false)}>
              关闭
            </BaseButton>
          </BaseSpace>
        }
      >
        <div style={{ textAlign: "center" }}>
          <QRCodeSVG
            value={qrCode}
            size={256}
            style={{ maxWidth: "100%" }}
            className="qr-code-svg"
          />
        </div>
      </Modal>
    </>
  );
};

export default CompanyPage;

export const useCompanyList = () => {
  const [companyList, setCompanyList] = useState<Company[]>([]);

  // 获取公司列表
  const fetchCompanyList = useCallback(async () => {
    try {
      const response = await adminApi.companies.list({
        page: 1,
        page_size: 100,
      });
      setCompanyList(response?.data?.companies || []);
    } catch (error: any) {
      console.error("error", error);
    }
  }, []);

  useUpdateEffect(() => {
    fetchCompanyList();
  }, []);

  return {
    companyList,
    fetchCompanyList,
  };
};
